import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Heart, ArrowRight, GraduationCap, HeartHandshake, DoorOpen, Sunrise, ChevronDown, Ticket } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES, ORG, QUOTE, WHO_WE_ARE, IMPACT } from "@/config/content";
import { useDonate } from "@/context/DonateContext";

const IMPACT_ICONS = [GraduationCap, HeartHandshake, DoorOpen, Sunrise];

const HeroText = () => {
  const reduce = useReducedMotion();
  const lines = ["Honoring a Legacy.", "Expanding Opportunity."];
  const container = { hidden: {}, show: { transition: { staggerChildren: 0.18, delayChildren: 0.2 } } };
  const line = {
    hidden: { y: "110%" },
    show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
  };
  return (
    <h1 className="font-serif font-light leading-[0.95] tracking-tight text-ivory text-5xl sm:text-6xl lg:text-7xl">
      {reduce ? (
        <>
          <span className="block">{lines[0]}</span>
          <span className="block gold-text">{lines[1]}</span>
        </>
      ) : (
        <motion.span variants={container} initial="hidden" animate="show" className="block">
          {lines.map((t, i) => (
            <span key={i} className="line-mask">
              <motion.span variants={line} className={`block ${i === 1 ? "gold-text" : ""}`}>{t}</motion.span>
            </span>
          ))}
        </motion.span>
      )}
    </h1>
  );
};

const Chapter = ({ n }) => (
  <span className="font-serif text-gold/50 text-lg">{n}</span>
);

const GoldBtn = ({ onClick, to, children, testid }) => {
  const cls = "inline-flex items-center justify-center gap-2 rounded-sm bg-gold px-8 py-4 text-base font-bold uppercase tracking-wider text-ink transition-colors hover:bg-goldhi";
  return to ? <Link to={to} className={cls} data-testid={testid}>{children}</Link>
    : <button onClick={onClick} className={cls} data-testid={testid}>{children}</button>;
};
const OutlineBtn = ({ to, onClick, children, testid }) => {
  const cls = "inline-flex items-center justify-center gap-2 rounded-sm border border-gold/50 px-8 py-4 text-base font-semibold uppercase tracking-wider text-gold transition-colors hover:bg-gold/10 hover:border-gold";
  return to ? <Link to={to} className={cls} data-testid={testid}>{children}</Link>
    : <button onClick={onClick} className={cls} data-testid={testid}>{children}</button>;
};

export default function Home() {
  const { openDonate } = useDonate();
  const reduce = useReducedMotion();

  const schema = {
    "@context": "https://schema.org",
    "@type": "NGO",
    name: ORG.fullName,
    alternateName: "EHSIMFFA",
    url: "https://www.emperorhaileselassie.org",
    description: ORG.mission,
    email: ORG.email,
    nonprofitStatus: "Nonprofit501c3",
    areaServed: "Ethiopia",
  };

  return (
    <div>
      <Seo
        title="Honoring a Legacy, Expanding Opportunity"
        description="EHSIMFFA supports higher education and scholarships for economically disadvantaged university students in Ethiopia, honoring the legacy of Emperor Haile Selassie I."
        path="/"
        schema={schema}
      />

      {/* SECTION 1 — HERO */}
      <section className="relative grain min-h-screen overflow-hidden flex items-center pt-28 pb-20" data-testid="hero-section">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_80%_20%,rgba(200,165,91,0.10),transparent_60%)]" />
        <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="relative z-10">
            <div className="kicker mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-gold/60" /> Established 2020 · A 501(c)(3) Foundation
            </div>
            <HeroText />
            <p className="mt-8 max-w-xl text-lg sm:text-xl leading-relaxed text-ivory2">
              We fund university scholarships for economically disadvantaged students throughout Ethiopia —
              carrying forward His Imperial Majesty's belief in the power of education.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <GoldBtn onClick={() => openDonate()} testid="hero-donate-btn"><Heart className="h-5 w-5" /> Donate Now</GoldBtn>
              <OutlineBtn to="/gala" testid="hero-gala-btn"><Ticket className="h-5 w-5" /> Gala Tickets 2026</OutlineBtn>
              <OutlineBtn to="/legacy" testid="hero-legacy-btn">Explore His Legacy <ArrowRight className="h-4 w-4" /></OutlineBtn>
            </div>
          </div>

          <div className="relative z-10">
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              className="relative mx-auto max-w-md"
            >
              <div className="absolute -inset-3 border border-gold/30" aria-hidden="true" />
              <div className="overflow-hidden bg-ink2">
                <motion.img
                  src={IMAGES.emperorColor}
                  alt="His Imperial Majesty Emperor Haile Selassie I"
                  className="aspect-[4/5] w-full object-cover object-top"
                  initial={reduce ? {} : { scale: 1.08 }}
                  animate={reduce ? {} : { scale: 1 }}
                  transition={{ duration: 12, ease: "easeOut" }}
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-4 left-6 right-6 bg-ink px-5 py-3 hairline">
                <p className="text-center font-serif text-lg text-ivory2">His Imperial Majesty Emperor Haile Selassie I</p>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold/60" aria-hidden="true">
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </div>
      </section>

      <Marquee />

      {/* SECTION 2 — WHO WE ARE */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid gap-14 lg:grid-cols-2 lg:items-center" data-testid="who-section">
        <Reveal className="relative order-2 lg:order-1">
          <div className="absolute -inset-3 border border-gold/20" aria-hidden="true" />
          <img src={IMAGES.emperorFormalBW} alt="His Imperial Majesty Emperor Haile Selassie I, formal portrait" className="relative w-full object-cover" loading="lazy" />
        </Reveal>
        <Reveal className="order-1 lg:order-2" delay={0.1}>
          <div className="kicker flex items-center gap-3 mb-5"><Chapter n="01" /> Who We Are</div>
          <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight text-ivory">
            Friends carrying a vision across continents.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-ivory2">{WHO_WE_ARE.lead}</p>
          <p className="mt-4 text-lg leading-relaxed text-ivory2/85">{WHO_WE_ARE.mission}</p>
          <div className="mt-8"><OutlineBtn to="/about" testid="who-cta">Learn About Our Foundation <ArrowRight className="h-4 w-4" /></OutlineBtn></div>
        </Reveal>
      </section>

      {/* SECTION 3 — THE LEGACY */}
      <section className="relative border-y border-gold/15 bg-ink2" data-testid="legacy-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="kicker flex items-center gap-3 mb-5"><Chapter n="02" /> His Legacy</div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight text-ivory">
              A monarch who believed a nation rises through its students.
            </h2>
            <blockquote className="mt-8 border-l-2 border-gold pl-6">
              <p className="font-serif text-2xl sm:text-3xl italic leading-snug text-ivory">"{QUOTE.text}"</p>
              <footer className="mt-4 text-gold">— {QUOTE.attribution}, {QUOTE.year}</footer>
            </blockquote>
            <div className="mt-8"><OutlineBtn to="/legacy" testid="legacy-cta">Explore His Legacy <ArrowRight className="h-4 w-4" /></OutlineBtn></div>
          </Reveal>
          <Reveal delay={0.1} className="relative">
            <div className="absolute -inset-3 border border-gold/20" aria-hidden="true" />
            <img src={IMAGES.emperorDesk} alt="Emperor Haile Selassie I at his desk" className="relative w-full object-cover grayscale-[10%]" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — EDUCATION CHANGES LIVES */}
      <section className="relative overflow-hidden" data-testid="education-section">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px]">
            <img src={IMAGES.graduates} alt="Emperor Haile Selassie I with university graduates" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/40 to-ink/70 lg:to-ink" />
          </div>
          <div className="bg-ink px-5 sm:px-12 py-24 sm:py-32 flex items-center">
            <Reveal>
              <div className="kicker flex items-center gap-3 mb-5"><Chapter n="03" /> Education Changes Lives</div>
              <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight text-ivory">
                A scholarship is more than tuition. It is the chance to finish.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-ivory2">
                We support economically disadvantaged college students so they can complete their studies —
                covering the basics like food, clothing, hygiene supplies, and transport. Without this support,
                the alternative for many gifted students is dropping out.
              </p>
              <div className="mt-8"><GoldBtn onClick={() => openDonate()} testid="education-cta"><Heart className="h-5 w-5" /> Support a Student</GoldBtn></div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 5 — OUR WORK IN ETHIOPIA (stat band) */}
      <section className="border-y border-gold/15 bg-forest/25" data-testid="work-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <Reveal className="max-w-3xl">
            <div className="kicker flex items-center gap-3 mb-5"><Chapter n="04" /> Our Work in Ethiopia</div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight text-ivory">
              Real students, real universities, a multi-year promise.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-3">
            {[
              { k: "200", l: "scholars supported across Ethiopia in 2025/26" },
              { k: "20", l: "universities and colleges reached across Ethiopia" },
              { k: "4–5 yr", l: "of support for each scholar, through graduation" },
            ].map((s, i) => (
              <Reveal key={s.k} delay={i * 0.1} className="border-t border-gold/30 pt-6">
                <div className="font-serif text-6xl gold-text">{s.k}</div>
                <p className="mt-3 text-ivory2 leading-relaxed">{s.l}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-12"><OutlineBtn to="/ethiopia" testid="work-cta">See Our Work in Ethiopia <ArrowRight className="h-4 w-4" /></OutlineBtn></div>
        </div>
      </section>

      {/* SECTION 6 — HOW YOUR DONATION HELPS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="impact-section">
        <Reveal className="max-w-3xl">
          <div className="kicker flex items-center gap-3 mb-5"><Chapter n="05" /> How Your Donation Helps</div>
          <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight text-ivory">Your support helps provide:</h2>
        </Reveal>
        <div className="mt-14 grid gap-px bg-gold/15 sm:grid-cols-2 lg:grid-cols-4 hairline">
          {IMPACT.map((item, i) => {
            const Icon = IMPACT_ICONS[i];
            return (
              <Reveal key={item.title} delay={i * 0.08} className="bg-ink p-8 group transition-colors hover:bg-ink2">
                <Icon className="h-9 w-9 text-gold transition-transform group-hover:-translate-y-1" />
                <h3 className="mt-6 font-serif text-2xl text-ivory">{item.title}</h3>
                <p className="mt-3 text-ivory2/85 leading-relaxed">{item.body}</p>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-14 text-center">
          <GoldBtn onClick={() => openDonate()} testid="impact-cta"><Heart className="h-5 w-5" /> Make a Difference — Donate Today</GoldBtn>
        </div>
      </section>

      {/* SECTION 7 — QUOTE / TRIBUTE */}
      <section className="relative grain overflow-hidden" data-testid="tribute-section">
        <img src={IMAGES.emperorFormalBW} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-top opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 py-28 sm:py-40 text-center">
          <Reveal>
            <div className="mx-auto mb-8 h-px w-16 bg-gold" />
            <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light italic leading-snug text-ivory">
              "{QUOTE.text}"
            </blockquote>
            <p className="mt-8 kicker">{QUOTE.attribution} · {QUOTE.year}</p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 8 — BOARD PREVIEW */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-24 sm:py-32 text-center" data-testid="board-preview">
        <Reveal>
          <div className="kicker flex items-center justify-center gap-3 mb-5"><Chapter n="06" /> Leadership</div>
          <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight text-ivory">
            Guided by a devoted board and community.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-ivory2">
            EHSIMFFA is stewarded by volunteers united by a shared devotion to His Imperial Majesty's vision and
            to the students of Ethiopia — working hand in hand with the Foundation in Ethiopia.
          </p>
          <div className="mt-8 flex justify-center"><OutlineBtn to="/board" testid="board-cta">Meet Our Board <ArrowRight className="h-4 w-4" /></OutlineBtn></div>
        </Reveal>
      </section>

      {/* SECTION 9 — FINAL DONATION CTA */}
      <section className="relative border-t border-gold/20 bg-ink2" data-testid="final-cta">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-24 sm:py-32 text-center">
          <Reveal>
            <div className="kicker mb-6">Help Carry the Legacy Forward</div>
            <h2 className="font-serif text-4xl sm:text-6xl font-light leading-tight text-ivory">
              A single gift can keep a scholar in the classroom.
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-ivory2">
              Your support expands educational opportunity for university students in Ethiopia — and honors a
              legacy built on the conviction that knowledge lifts a nation.
            </p>
            <div className="mt-10 flex justify-center">
              <GoldBtn onClick={() => openDonate()} testid="final-donate-btn"><Heart className="h-5 w-5" /> Donate Now</GoldBtn>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
